import React, { useState, useContext, useMemo, useCallback } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  Search, 
  Mail, 
  PhoneCall, 
  Plus, 
  Lock, 
  Star, 
  X, 
  Copy, 
  Check,
  Send,
  Users
} from 'lucide-react';

const isRetiredEmployee = (u) => {
  if (!u) return true;
  if (u.UserID === 'system-config') return true;
  const status = (u.Status || u.status || '').toUpperCase();
  if (status === 'RETIRED' || status === 'RESIGNED' || status === 'INACTIVE' || u.IsRetired === true || u.Resigned === true || u.IsResigned === true) {
    return true;
  }
  return false;
};

const MobileAddressBookTab = ({ onComposeTo }) => {
  const { users, currentUser, customers, addCustomer, updateCustomer, deleteCustomer } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('INTERNAL'); // 'INTERNAL' | 'ALL_CUSTOMERS' | 'PRIVATE' | 'VIP'
  const [selectedDept, setSelectedDept] = useState('ALL');
  
  // Modal states
  const [detailModalContact, setDetailModalContact] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Form states
  const [custVisibility, setCustVisibility] = useState('PUBLIC');
  const [custCategory, setCustCategory] = useState('VIP');
  const [custName, setCustName] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custCompany, setCustCompany] = useState('');
  const [custMemo, setCustMemo] = useState('');

  // Active (Non-retired) Employees
  const activeEmployees = useMemo(() => {
    return (users || []).filter(u => !isRetiredEmployee(u)).map(u => ({ ...u, _isInternal: true }));
  }, [users]);

  // Check if private contact is visible to current logged-in user
  const isContactVisibleToMe = useCallback((c) => {
    if (c.Visibility === 'PRIVATE') {
      const myId = currentUser?.UserID;
      const myEmail = (currentUser?.Email || '').toLowerCase();
      return (c.OwnerID && c.OwnerID === myId) || 
             (c.OwnerID && c.OwnerID.toLowerCase() === myEmail) || 
             (c.CreatedBy && c.CreatedBy === currentUser?.Name) ||
             (!c.OwnerID && !c.CreatedBy);
    }
    return true;
  }, [currentUser]);

  const visibleCustomers = useMemo(() => {
    return (customers || []).filter(isContactVisibleToMe).map(c => ({ ...c, _isInternal: false }));
  }, [customers, isContactVisibleToMe]);

  const departments = ['ALL', ...Array.from(new Set(activeEmployees.map(u => u.Department).filter(Boolean)))];

  // Filtered contacts list
  const filteredList = useMemo(() => {
    let list = [];
    if (activeTab === 'INTERNAL') {
      list = activeEmployees.filter(u => selectedDept === 'ALL' || u.Department === selectedDept);
    } else if (activeTab === 'ALL_CUSTOMERS') {
      list = visibleCustomers;
    } else if (activeTab === 'PRIVATE') {
      list = visibleCustomers.filter(c => c.Visibility === 'PRIVATE');
    } else if (activeTab === 'VIP') {
      list = visibleCustomers.filter(c => c.Category === 'VIP' || !c.Category);
    }

    const s = searchTerm.toLowerCase().trim();
    if (!s) return list;
    return list.filter(item => {
      return (
        (item.Name || '').toLowerCase().includes(s) ||
        (item.Department || item.Company || '').toLowerCase().includes(s) ||
        (item.JobTitle || item.Position || '').toLowerCase().includes(s) ||
        (item.Email || '').toLowerCase().includes(s) ||
        (item.Phone || '').includes(s) ||
        (item.Memo || '').toLowerCase().includes(s)
      );
    });
  }, [activeTab, activeEmployees, visibleCustomers, selectedDept, searchTerm]);

  const handleCopyText = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleOpenAddModal = () => {
    setEditingCustomer(null);
    setCustVisibility(activeTab === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC');
    setCustCategory('VIP');
    setCustName('');
    setCustEmail('');
    setCustPhone('');
    setCustCompany('');
    setCustMemo('');
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (customer) => {
    setEditingCustomer(customer);
    setCustVisibility(customer.Visibility || 'PUBLIC');
    setCustCategory(customer.Category || 'VIP');
    setCustName(customer.Name || '');
    setCustEmail(customer.Email || '');
    setCustPhone(customer.Phone || '');
    setCustCompany(customer.Company || '');
    setCustMemo(customer.Memo || '');
    setIsFormModalOpen(true);
    setDetailModalContact(null);
  };

  const handleSaveCustomerSubmit = (e) => {
    e.preventDefault();
    if (!custName.trim() || !custEmail.trim()) {
      alert('고객 이름과 이메일은 필수 입력 항목입니다.');
      return;
    }

    if (editingCustomer) {
      const updated = {
        ...editingCustomer,
        Name: custName.trim(),
        Email: custEmail.trim(),
        Phone: custPhone.trim(),
        Company: custCompany.trim(),
        Category: custCategory,
        Visibility: custVisibility,
        Memo: custMemo.trim()
      };
      if (typeof updateCustomer === 'function') {
        updateCustomer(updated);
      }
      setIsFormModalOpen(false);
      setEditingCustomer(null);
      alert('연락처가 수정되었습니다.');
    } else {
      addCustomer({
        Name: custName.trim(),
        Email: custEmail.trim(),
        Phone: custPhone.trim(),
        Company: custCompany.trim(),
        Category: custCategory || 'VIP',
        Visibility: custVisibility,
        Memo: custMemo.trim(),
        OwnerID: currentUser?.UserID || currentUser?.Email,
        CreatedBy: currentUser?.Name || '본인'
      });
      setIsFormModalOpen(false);
      alert(custVisibility === 'PRIVATE' ? '나만 보는 비공개 연락처가 등록되었습니다.' : '새 연락처가 등록되었습니다.');
    }
  };

  const handleDeleteCustomer = (customer) => {
    if (window.confirm(`'${customer.Name}' 연락처를 삭제하시겠습니까?`)) {
      if (typeof deleteCustomer === 'function') {
        deleteCustomer(customer.CustomerID);
      }
      setDetailModalContact(null);
      alert('연락처가 삭제되었습니다.');
    }
  };

  return (
    <div className="p-4 space-y-3.5 text-left pb-24 bg-slate-50 min-h-full">
      
      {/* 1. Header & Top Action */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold">
            <Users className="w-4 h-4" />
          </div>
          <h1 className="text-lg font-black text-slate-900">주소록 & 고객</h1>
        </div>

        <button
          type="button"
          onClick={handleOpenAddModal}
          className="px-3 py-1.5 bg-indigo-600 text-white font-bold text-xs rounded-xl flex items-center gap-1 shadow-2xs cursor-pointer active:scale-95"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>등록</span>
        </button>
      </div>

      {/* 2. Main Filter Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
        <button
          type="button"
          onClick={() => setActiveTab('INTERNAL')}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
            activeTab === 'INTERNAL' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'bg-white border border-slate-200 text-slate-600'
          }`}
        >
          사내직원 ({activeEmployees.length})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('ALL_CUSTOMERS')}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
            activeTab === 'ALL_CUSTOMERS' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'bg-white border border-slate-200 text-slate-600'
          }`}
        >
          공유 고객 ({visibleCustomers.filter(c => c.Visibility !== 'PRIVATE').length})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('PRIVATE')}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
            activeTab === 'PRIVATE' ? 'bg-amber-600 text-white shadow-2xs font-black' : 'bg-white border border-slate-200 text-slate-600'
          }`}
        >
          <Lock className="w-3 h-3" />
          <span>나만보기 ({visibleCustomers.filter(c => c.Visibility === 'PRIVATE').length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('VIP')}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
            activeTab === 'VIP' ? 'bg-indigo-600 text-white shadow-2xs font-black' : 'bg-white border border-slate-200 text-slate-600'
          }`}
        >
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          <span>VIP</span>
        </button>
      </div>

      {/* 3. Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="이름, 부서/회사, 이메일, 전화번호 검색..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-200 rounded-2xl text-xs focus:outline-none focus:border-indigo-600 shadow-2xs font-medium"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-2.5 text-slate-400 font-bold text-xs"
          >
            ✕
          </button>
        )}
      </div>

      {/* Department Filter for Internal Employees */}
      {activeTab === 'INTERNAL' && (
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
          {departments.map(dept => (
            <button
              key={dept}
              type="button"
              onClick={() => setSelectedDept(dept)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all ${
                selectedDept === dept
                  ? 'bg-slate-800 text-white shadow-2xs'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {dept === 'ALL' ? '전체 부서' : dept}
            </button>
          ))}
        </div>
      )}

      {/* 4. Contact Cards List */}
      <div className="space-y-2.5">
        {filteredList.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 text-slate-400 text-xs space-y-2">
            <div>👥 등록된 연락처가 없습니다.</div>
            {activeTab !== 'INTERNAL' && (
              <button
                type="button"
                onClick={handleOpenAddModal}
                className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-xl text-xs font-bold inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                <span>연락처 추가</span>
              </button>
            )}
          </div>
        ) : (
          filteredList.map(item => {
            const itemKey = item.UserID || item.CustomerID;
            const isPrivate = item.Visibility === 'PRIVATE';

            return (
              <div 
                key={itemKey} 
                onClick={() => setDetailModalContact(item)}
                className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs flex items-center justify-between gap-3 cursor-pointer active:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 overflow-hidden flex-1">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 font-black flex items-center justify-center flex-shrink-0 text-sm">
                    {item.Name?.slice(0, 1) || '연'}
                  </div>
                  <div className="overflow-hidden flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-xs text-slate-900 truncate">{item.Name}</span>
                      {item._isInternal ? (
                        <span className="text-[10px] px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded-md font-semibold">
                          {item.JobTitle || item.Position || '사원'}
                        </span>
                      ) : isPrivate ? (
                        <span className="text-[10px] px-1.5 py-0.2 bg-amber-50 text-amber-700 border border-amber-200 rounded-md font-bold flex items-center gap-0.5">
                          <Lock className="w-2.5 h-2.5" />
                          <span>나만보기</span>
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.2 bg-indigo-50 text-indigo-700 rounded-md font-semibold">
                          {item.Category || '고객'}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {item._isInternal ? (item.Department || '부서 미정') : (item.Company || '개인 고객')}
                    </div>
                    <div className="text-[11px] text-indigo-600 font-mono truncate font-semibold">
                      {item.Email}
                    </div>
                  </div>
                </div>

                {/* Quick Dial / Mail Buttons */}
                <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {item.Phone && (
                    <a
                      href={`tel:${item.Phone}`}
                      className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shadow-2xs active:scale-95 transition-transform"
                      title="전화 걸기"
                    >
                      <PhoneCall className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (onComposeTo) {
                        onComposeTo(item.Email);
                      }
                      window.dispatchEvent(new CustomEvent('trigger-compose', {
                        detail: { to: item.Email }
                      }));
                    }}
                    className="w-8 h-8 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center shadow-2xs active:scale-95 transition-transform cursor-pointer"
                    title="메일 작성"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 5. Mobile Detail Popup Modal */}
      {detailModalContact && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full shadow-2xl space-y-4 text-left animate-in zoom-in-95 duration-150">
            <div className="flex items-start justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white font-black flex items-center justify-center text-lg">
                  {detailModalContact.Name?.slice(0, 1)}
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">{detailModalContact.Name}</h3>
                  <span className="text-xs text-indigo-600 font-bold block">
                    {detailModalContact._isInternal ? `${detailModalContact.Department} • ${detailModalContact.JobTitle || '사원'}` : (detailModalContact.Company || '외부 고객')}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailModalContact(null)}
                className="p-1 text-slate-400 hover:text-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-slate-400 font-bold block">이메일</span>
                  <span className="font-mono text-slate-900 font-bold">{detailModalContact.Email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyText(detailModalContact.Email, 'email')}
                  className="p-1 text-slate-400 hover:text-indigo-600"
                >
                  {copiedField === 'email' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>

              {detailModalContact.Phone && (
                <div className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold block">전화번호</span>
                    <span className="font-mono text-slate-900 font-bold">{detailModalContact.Phone}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyText(detailModalContact.Phone, 'phone')}
                    className="p-1 text-slate-400 hover:text-indigo-600"
                  >
                    {copiedField === 'phone' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}

              {detailModalContact.Memo && (
                <div className="p-2.5 bg-indigo-50/60 rounded-xl">
                  <span className="text-[10px] text-indigo-900 font-bold block mb-1">메모</span>
                  <p className="text-slate-700 whitespace-pre-wrap text-[11px]">{detailModalContact.Memo}</p>
                </div>
              )}
            </div>

            <div className="pt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setDetailModalContact(null);
                  if (onComposeTo) onComposeTo(detailModalContact.Email);
                  window.dispatchEvent(new CustomEvent('trigger-compose', { detail: { to: detailModalContact.Email } }));
                }}
                className="flex-1 py-2.5 bg-indigo-600 text-white font-bold text-xs rounded-xl shadow-xs flex items-center justify-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                <span>메일 작성</span>
              </button>

              {detailModalContact.Phone && (
                <a
                  href={`tel:${detailModalContact.Phone}`}
                  className="flex-1 py-2.5 bg-emerald-600 text-white font-bold text-xs rounded-xl shadow-xs flex items-center justify-center gap-1.5"
                >
                  <PhoneCall className="w-3.5 h-3.5" />
                  <span>전화 걸기</span>
                </a>
              )}
            </div>

            {!detailModalContact._isInternal && (
              <div className="pt-2 flex items-center justify-between border-t border-slate-100 text-xs">
                <button
                  type="button"
                  onClick={() => handleDeleteCustomer(detailModalContact)}
                  className="text-rose-600 font-bold text-xs"
                >
                  삭제
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenEditModal(detailModalContact)}
                  className="text-slate-700 font-bold text-xs"
                >
                  수정
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6. Mobile Add / Edit Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full shadow-2xl space-y-3.5 text-left animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">
                {editingCustomer ? '연락처 수정' : '새 연락처 등록'}
              </h3>
              <button
                type="button"
                onClick={() => setIsFormModalOpen(false)}
                className="p-1 text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCustomerSubmit} className="space-y-3">
              {/* Visibility selection */}
              <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setCustVisibility('PUBLIC')}
                  className={`py-1.5 text-xs font-bold rounded-lg transition-all ${custVisibility === 'PUBLIC' ? 'bg-white text-indigo-700 shadow-2xs' : 'text-slate-500'}`}
                >
                  🌐 전사공유
                </button>
                <button
                  type="button"
                  onClick={() => setCustVisibility('PRIVATE')}
                  className={`py-1.5 text-xs font-bold rounded-lg transition-all ${custVisibility === 'PRIVATE' ? 'bg-white text-amber-700 shadow-2xs' : 'text-slate-500'}`}
                >
                  🔒 나만보기
                </button>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 block mb-0.5">성명 *</label>
                <input
                  type="text"
                  placeholder="성명"
                  value={custName}
                  onChange={e => setCustName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 block mb-0.5">이메일 *</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={custEmail}
                  onChange={e => setCustEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 block mb-0.5">전화번호</label>
                <input
                  type="text"
                  placeholder="010-xxxx-xxxx"
                  value={custPhone}
                  onChange={e => setCustPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 block mb-0.5">회사 / 소속</label>
                <input
                  type="text"
                  placeholder="회사명"
                  value={custCompany}
                  onChange={e => setCustCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 block mb-0.5">메모</label>
                <textarea
                  rows={2}
                  placeholder="메모 입력"
                  value={custMemo}
                  onChange={e => setCustMemo(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 resize-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-3 py-2 bg-slate-100 text-slate-600 font-bold text-xs rounded-xl"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 text-white font-bold text-xs rounded-xl shadow-xs"
                >
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default MobileAddressBookTab;
